import React, { useState } from 'react';
import { Alert, Dropdown, Spinner } from 'react-bootstrap';
import {
  useEticketingCreateRoutePriceMutation,
  useEticketingUpdateRoutePriceMutation,
  useEticketingDeleteRoutePriceMutation,
} from '../../../../api/e-ticketing/e-ticketing.hooks';
import { emojis } from '../../bootstrap/alertData';
import DeleteModal from '../../DeleteModal';
import { Can } from '../../../../context/Can';

const RoutePriceForm = ({ prices, routeId, onRefresh }) => {
  const { create: createPrice, isLoading: creating } = useEticketingCreateRoutePriceMutation();
  const { update: updatePrice, isLoading: updating } = useEticketingUpdateRoutePriceMutation();
  const { remove: deletePrice, isLoading: deleting } = useEticketingDeleteRoutePriceMutation();

  const [showForm, setShowForm] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  const [formData, setFormData] = useState({
    description: '',
    price: "",
    cloud_visibility: true,
    ticket_booth_visibility: true,
  });
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [priceToDelete, setPriceToDelete] = useState(null);

  const isEditMode = !!editingPrice;

  const showAlert = (message, variant = 'success') => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 3000);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddNew = () => {
    setEditingPrice(null);
    setFormData({ description: '', price: 0, cloud_visibility: true, ticket_booth_visibility: true });
    setShowForm(true);
  };

  const handleEdit = (price) => {
    setEditingPrice(price);
    setFormData({
      description: price.description || '',
      price: price.price || 0,
      cloud_visibility: price.cloud_visibility !== undefined ? price.cloud_visibility : true,
      ticket_booth_visibility: price.ticket_booth_visibility !== undefined ? price.ticket_booth_visibility : true,
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingPrice(null);
    setFormData({ description: '', price: 0, cloud_visibility: true, ticket_booth_visibility: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const submitData = {
      description: formData.description,
      price: formData.price,
      cloud_visibility: formData.cloud_visibility,
      ticket_booth_visibility: formData.ticket_booth_visibility,
      route_id: routeId,
    };

    try {
      if (isEditMode) {
        await updatePrice(editingPrice.id, submitData);
        showAlert('Price updated successfully!');
      } else {
        await createPrice(submitData);
        showAlert('Price added successfully!');
      }
      onRefresh();
      handleCancel();
    } catch (err) {
      showAlert(err?.message || `Failed to ${isEditMode ? 'update' : 'create'} price.`, 'danger');
    }
  };

  const handleDeleteClick = (price) => {
    setPriceToDelete(price);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!priceToDelete) return;

    try {
      await deletePrice(priceToDelete.id);
      showAlert('Price deleted successfully!');
      onRefresh();
      setShowDeleteModal(false);
      setPriceToDelete(null);
    } catch (err) {
      showAlert(err?.message || 'Failed to delete price.', 'danger');
    }
  };

  return (
    <div>
      {alert.show && (
        <Alert variant={alert.variant} dismissible onClose={() => setAlert({ show: false, message: '', variant: '' })}>
          {alert.variant === 'success' ? emojis.success : emojis.warning} {alert.message}
        </Alert>
      )}

      {showForm ? (
        <div className="card">
          <div className="card-header">
            <h5 className="card-title m-0">{isEditMode ? 'Edit Price' : 'Add Price'}</h5>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-xl-12">
                  <div className="mb-3 row">
                    <label className="col-lg-2 col-form-label">
                      Description<span className="text-danger">*</span>
                    </label>
                    <div className="col-lg-10">
                      <input
                        type="text"
                        name="description"
                        value={formData.description}
                        onChange={handleFormChange}
                        className="form-control"
                        placeholder="e.g., General (12+ Years), Student, Senior Citizens 65+"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="col-xl-6">
                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">
                      Price (Rs.)<span className="text-danger">*</span>
                    </label>
                    <div className="col-lg-8">
                      <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleFormChange}
                        className="form-control"
                        placeholder="Enter price"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="col-xl-6">
                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">Cloud Visibility</label>
                    <div className="col-lg-8">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="cloud_visibility"
                          checked={formData.cloud_visibility}
                          onChange={(e) => setFormData({ ...formData, cloud_visibility: e.target.checked })}
                        />
                        <label className="form-check-label">
                          {formData.cloud_visibility ? 'Visible' : 'Hidden'}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-6">
                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">Ticket Booth Visibility</label>
                    <div className="col-lg-8">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="ticket_booth_visibility"
                          checked={formData.ticket_booth_visibility}
                          onChange={(e) => setFormData({ ...formData, ticket_booth_visibility: e.target.checked })}
                        />
                        <label className="form-check-label">
                          {formData.ticket_booth_visibility ? 'Visible' : 'Hidden'}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-3 row">
                  <div className="col-lg-10 ms-auto">
                    <button type="submit" className="btn btn-secondary" disabled={creating || updating}>
                      {creating || updating ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          {isEditMode ? 'Updating...' : 'Creating...'}
                        </>
                      ) : (
                        'Save'
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary ms-2"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title m-0">Route Prices</h5>
            <Can I="create" a="Route Price">
            <button className="btn btn-secondary btn-sm" onClick={handleAddNew}>
              <i className="fa fa-plus me-1"></i> Add Price
            </button>
            </Can>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Description</th>
                    <th>Price (Rs.)</th>
                    <th>Cloud Visibility</th>
                    <th>Ticket Booth Visibility</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {prices.length > 0 ? (
                    prices.map((price, index) => (
                      <tr key={price.id}>
                        <td>{index + 1}</td>
                        <td>{price.description || "--"}</td>
                        <td><strong>Rs. {price.price?.toLocaleString() || 0}</strong></td>
                        <td>
                          <span className={`badge ${price.cloud_visibility ? 'bg-success' : 'bg-secondary'}`}>
                            {price.cloud_visibility ? 'Visible' : 'Hidden'}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${price.ticket_booth_visibility ? 'bg-success' : 'bg-secondary'}`}>
                            {price.ticket_booth_visibility ? 'Visible' : 'Hidden'}
                          </span>
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle variant="link">
                              &#x2022;&#x2022;&#x2022;
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end">
                              <Dropdown.Item onClick={() => handleEdit(price)}>
                                Edit
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => handleDeleteClick(price)}>
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No prices found for this route.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <DeleteModal
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setPriceToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Price"
        message={`Are you sure you want to delete this price (${priceToDelete?.description} - Rs. ${priceToDelete?.price})? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleting}
        danger={true}
      />
    </div>
  );
};

export default RoutePriceForm;
